import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import Box from '@mui/material/Box/Box';
import { FcCheckmark } from 'react-icons/fc';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import Autocomplete from '@mui/material/Autocomplete/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';

import { Input } from '../../../Input/Input';
import { AddressElementUtils } from './utils';
import { FullAddres } from '../../../../contracts';
import { useAPI } from '../../../../services/api.service';
import { InputField } from '../../../InputField/InputField';
import { useCheckout } from '../../../../hooks/use-checkout';
import { usePaymentState } from '../../hooks/use-payment-state';
import { useAppConfig } from '../../../../hooks/use-app-config';
import { useAddressSuggestions } from './hooks/use-address-suggestions';

import './styles.scss';

export interface AddressForm {
  address?: string;
  zipCode?: string;
}

interface AddressElementProps {
  zipOnly: boolean;
  onValidationChange?: (isValid: boolean) => void;
  billingAddressError?: string;
  onBlur?: () => void;
}

export const AddressElement = forwardRef(
  ({ zipOnly, onValidationChange, billingAddressError, onBlur }: AddressElementProps, ref) => {
    const { API } = useAPI();
    const { checkoutData } = useCheckout();
    const zipCode = usePaymentState((state) => state.zipCode);
    const fullAddress = usePaymentState((state) => state.fullAddress);
    const setFullAddress = usePaymentState((state) => state.setFullAddress);
    const isPaymentLoading = usePaymentState((state) => state.isPaymentLoading);
    const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
    const setPriceBreakdown = usePaymentState((state) => state.setPriceBreakdown);
    const setCustomerGeoState = usePaymentState((state) => state.setCustomerGeoState);
    const setIsZipCodeValidatedOnce = usePaymentState((state) => state.setIsZipCodeValidatedOnce);
    const [fullAddressValue, setFullAddressValue] = useState<any>({
      zipCode: zipCode,
      address: fullAddress,
    });
    const {
      isLoading,
      suggestions,
      removeSuggestions,
      fetchAddressSuggestions,
      isAddressSuggestionsNotAvailable,
    } = useAddressSuggestions();
    const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
    const previousValuesRef = useRef<any>({ address: fullAddress, zipCode: zipCode });
    const { featureFlags } = useAppConfig();
    const shouldHideFieldsLabels = featureFlags?.checkout_remove_field_labels;
    const previousErrorsRef = useRef<any>({});
    const countryCode2 =
      checkoutData?.customer.taxCountry || checkoutData?.customer.geoLocator.countryCode2;
    const isCanada = countryCode2 === 'CA';
    const isUSA = countryCode2 === 'US';

    const formik = useFormik({
      initialValues: {
        address: fullAddress || '',
        zipCode: zipCode,
      },
      initialErrors: AddressElementUtils.getInitialErrors({
        isTaxValid: Boolean(priceBreakdown?.isTaxValid),
        zipOnly: zipOnly,
        fullAddress: fullAddress || '',
        countryCode2: countryCode2 || '',
        zipCode: zipCode || '',
      }),
      validateOnMount: true,
      validateOnBlur: false,
      validate: async (values) => {
        const previousValues = { ...previousValuesRef.current };
        const errors: any = {};
        const validationSchema = AddressElementUtils.getValidationSchema(countryCode2, zipOnly);

        if (!zipOnly && !isAddressSuggestionsNotAvailable) {
          await validationSchema
            .validateAt('address', {
              address: values.address,
            })
            .catch((err) => {
              errors.address = err.message;
            });
        }

        if (values.zipCode !== previousValues.zipCode) {
          await validationSchema
            .validateAt('zipCode', {
              zipCode: values.zipCode,
            })
            .then(async () => {
              const estimatedTaxResponse = await API.getEstimatedTaxforZipCode(values.zipCode);
              if (estimatedTaxResponse.isValid) {
                setPriceBreakdown(estimatedTaxResponse.priceBreakdown);
                setCustomerGeoState(estimatedTaxResponse.state);
              } else {
                formik.setFieldTouched('zipCode', true, false);
                errors.zipCode = t('fieldErrors.invalidZipCode');
              }
            })
            .catch((err) => {
              errors.zipCode = err.message;
            });
        } else {
          await validationSchema
            .validateAt('zipCode', {
              zipCode: values.zipCode,
            })
            .then(() => {
              if (previousErrorsRef.current.zipCode) {
                errors.zipCode = previousErrorsRef.current.zipCode;
              }
            })
            .catch((err) => {
              errors.zipCode = err.message;
            });
        }

        previousValuesRef.current = values;
        previousErrorsRef.current = errors;
        return errors;
      },
      onSubmit: () => {},
    });

    useEffect(() => {
      if (formik.isValid) {
        setIsZipCodeValidatedOnce();
      }
      onValidationChange?.(formik.isValid);
    }, [formik.isValid]);

    useImperativeHandle(
      ref,
      () => ({ isValid: () => formik.isValid, validate: formik.submitForm, values: formik.values }),
      [formik.isValid, formik.validateForm, formik.values]
    );

    const onValueChange = async (newValue: FullAddres) => {
      setFullAddressValue(newValue);
      removeSuggestions();
      if (newValue) {
        try {
          const estimatedTaxResponse = await API.getEstimatedTaxforAddress(
            newValue.address,
            newValue.zipCode
          );
          if (estimatedTaxResponse.isValid) {
            setFullAddress(newValue.address);
            previousValuesRef.current.zipCode = estimatedTaxResponse.zipCode;
            formik.setFieldValue('zipCode', estimatedTaxResponse.zipCode, true);
            setPriceBreakdown(estimatedTaxResponse.priceBreakdown);
            setCustomerGeoState(estimatedTaxResponse.state);
          }
        } catch (error) {
          console.error(error);
        }
      }
    };

    const handleBlur = (e: React.FocusEvent) => {
      formik.handleBlur(e);
      if (onBlur) {
        onBlur();
      }
    };

    return (
      <FormikProvider value={formik}>
        {!zipOnly && (
          <Autocomplete
            disabled={isPaymentLoading || isAddressSuggestionsNotAvailable}
            value={fullAddressValue}
            inputValue={formik.values.address}
            options={suggestions}
            freeSolo
            filterOptions={(x) => x}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.address)}
            onInputChange={(_, inputValue, reason) => {
              // const shouldValidateField = reason !== 'reset';
              formik.setFieldValue('address', inputValue.trimStart(), true);
              if (
                inputValue.length >= AddressElementUtils.MIN_CHARACTERS_FOR_SEARCH &&
                reason === 'input'
              ) {
                fetchAddressSuggestions(inputValue);
              }
            }}
            onChange={(_, newValue) => onValueChange(newValue)}
            renderInput={(params) => (
              <Box ref={params.InputProps.ref}>
                <Input
                  error={
                    isAddressSuggestionsNotAvailable
                      ? AddressElementUtils.ADDRESS_SEARCH_FAIL_MESSAGE
                      : formik.touched.address
                      ? formik.errors.address?.toString()
                      : ''
                  }
                  inputProps={{
                    ...params.inputProps,
                    placeholder: 'Address',
                    onBlur: handleBlur,
                    name: 'address',
                  }}
                  label={shouldHideFieldsLabels ? '' : 'Address'}
                  icon={isLoading ? <CircularProgress size={13} /> : null}
                />
              </Box>
            )}
          />
        )}
        {billingAddressError && <p className="p-FieldError">{billingAddressError}</p>}

        <InputField
          name={'zipCode'}
          label={shouldHideFieldsLabels ? '' : isUSA ? t('zip') : t('postalCode')}
          icon={
            formik.isValidating ? (
              <CircularProgress size={13} />
            ) : !formik.errors.zipCode ? (
              <FcCheckmark size={14} />
            ) : null
          }
          inputProps={{
            id: 'Field-postalCodeInput',
            placeholder: isUSA ? t('zip') : t('postalCode'),
            maxLength: isCanada ? 7 : 10,
            readOnly: formik.isValidating,
            onBlur: handleBlur,
            onChange: (e) => {
              if (isCanada) {
                let value = e.target.value.replace(/\s/g, '').toUpperCase();
                if (value.length > 3) {
                  value = `${value.slice(0, 3)} ${value.slice(3)}`;
                }
                formik.setFieldValue('zipCode', value, false);
              }
            },
          }}
        />
      </FormikProvider>
    );
  }
);

AddressElement.displayName = 'AddressElement';
